/**
 * DocSearchBar — Full-width search input for documentation.
 */
import { useState, useRef, useCallback, type ChangeEvent, type KeyboardEvent } from 'react';
import { createElement } from 'react';

interface DocSearchBarProps {
  productName: string;
  onSearch: (query: string) => void;
  onClear: () => void;
}

export function DocSearchBar({ productName, onSearch, onClear }: DocSearchBarProps) {
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const q = e.target.value;
      setValue(q);
      if (q.trim()) {
        onSearch(q);
      } else {
        onClear();
      }
    },
    [onSearch, onClear],
  );

  const handleClear = useCallback(() => {
    setValue('');
    onClear();
    inputRef.current?.focus();
  }, [onClear]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Escape') {
        handleClear();
      }
    },
    [handleClear],
  );

  return createElement(
    'div',
    {
      style: {
        position: 'relative' as const,
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        height: '48px',
        backgroundColor: 'var(--sw-bg-surface)',
        borderRadius: 'var(--sw-border-radius)',
        border: '1px solid var(--sw-border)',
        padding: '0 12px',
        boxSizing: 'border-box' as const,
      },
    },
    // Search icon (left)
    createElement(
      'svg',
      {
        width: 18,
        height: 18,
        viewBox: '0 0 24 24',
        fill: 'none',
        stroke: 'var(--sw-text-secondary)',
        strokeWidth: 2,
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        style: { flexShrink: 0, marginRight: '8px' },
        'aria-hidden': 'true',
      },
      createElement('circle', { cx: 11, cy: 11, r: 8 }),
      createElement('line', { x1: 21, y1: 21, x2: 16.65, y2: 16.65 }),
    ),
    // Input
    createElement('input', {
      ref: inputRef,
      type: 'search',
      value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      placeholder: `Search ${productName} docs...`,
      'aria-label': `Search ${productName} documentation`,
      style: {
        flex: 1,
        border: 'none',
        outline: 'none',
        background: 'transparent',
        color: 'var(--sw-text-primary)',
        fontSize: '14px',
        fontFamily: 'var(--sw-font-family)',
        minWidth: 0,
      },
    }),
    // Clear button (right, shown when value is non-empty)
    value.length > 0
      ? createElement(
          'button',
          {
            type: 'button',
            onClick: handleClear,
            'aria-label': 'Clear search',
            style: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '28px',
              height: '28px',
              minWidth: '28px',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              color: 'var(--sw-text-secondary)',
              padding: 0,
              marginLeft: '4px',
            },
          },
          createElement(
            'svg',
            {
              width: 16,
              height: 16,
              viewBox: '0 0 24 24',
              fill: 'none',
              stroke: 'currentColor',
              strokeWidth: 2,
              strokeLinecap: 'round',
              strokeLinejoin: 'round',
            },
            createElement('line', { x1: 18, y1: 6, x2: 6, y2: 18 }),
            createElement('line', { x1: 6, y1: 6, x2: 18, y2: 18 }),
          ),
        )
      : null,
  );
}
